/* imageCompress — client-side image downscale & JPEG re-encode.
   Modern smartphone photos are often 4-12 MB, which fail the ≤2 MB check
   in avatar upload flows. This helper reads any user-picked file, draws it
   into an offscreen canvas resized to fit within `maxSide` (long edge) and
   exports it as a JPEG data URL at the given quality. Typical 12 MP shots
   compress to ~200-400 KB and load blazingly fast.

   Usage:
     const dataUrl = await compressImage(file, { maxSide: 1280, quality: 0.85 });
     // dataUrl is ready to be sent to /api/users/me { photo_url: dataUrl }
*/

export async function compressImage(file, { maxSide = 1280, quality = 0.85 } = {}) {
  if (!file) throw new Error("no-file");
  if (!file.type?.startsWith("image/")) throw new Error("not-an-image");

  // Fast-path: tiny files (< 300 KB) — skip the canvas roundtrip.
  if (file.size < 300 * 1024) {
    return readAsDataURL(file);
  }

  const dataUrl = await readAsDataURL(file);
  const img = await decodeWithOrientation(file, dataUrl);

  let { width, height } = img;
  const longSide = Math.max(width, height);
  if (longSide > maxSide) {
    const scale = maxSide / longSide;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  // Improve downscale quality (browsers vary; this hints high-quality).
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // Paint a white background so transparent PNGs don't turn black when
  // encoded as JPEG.
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  // Encode as JPEG — much smaller than PNG for photos, universally supported.
  return canvas.toDataURL("image/jpeg", quality);
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error || new Error("read-failed"));
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode-failed"));
    img.src = src;
  });
}

/* Decode picked file honoring EXIF orientation so portrait phone photos
   render right-side-up on the canvas. Modern browsers implement
   createImageBitmap with imageOrientation:'from-image' — we prefer that
   path and fall back to the raw <img> loader when unavailable. */
async function decodeWithOrientation(file, dataUrlFallback) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch { /* fall through to <img> loader */ }
  }
  return loadImage(dataUrlFallback);
}

/* Convenience: estimate the byte size of a data URL (base64 → bytes). */
export function dataUrlBytes(dataUrl) {
  if (!dataUrl?.startsWith("data:")) return 0;
  const b64 = dataUrl.split(",", 2)[1] || "";
  return Math.floor((b64.length * 3) / 4);
}
